import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';

import { LoggerFactory } from '../logger/logger-factory.service';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { StorageProvider } from '../storage/storage';
import { CrActiviteInterne } from '../../models/activite-interne/activite-interne.model';
import { AlertController, LoadingController } from '@ionic/angular';
import { Events } from '../events/events.provider';
import { Router } from '@angular/router';
import { ChronoInput, IChrono } from '../../models/chrono.model';
import { UtilsServices } from '../../components/utils-services';
import { OtWithStiProvider } from '../ots-sti/ots-sti';
import { ActionEtapeSTI, StiProvider } from '../sti/sti.provider';

/**
 * Etat d'avancement du chrono
 * (le chrono met à jour ses counters seulement quand une étape
 * est mise en pause ou terminée donc on est obligé de les recalculer)
 */
export interface ICounters {
  /**
   * Temps de déplacement aller en secondes
   */
  counter_travel: number;
  /**
   * Temps d'intervention en secondes
   */
  counter_intervention: number;
  /**
   * Temps de déplacement retour en secondes
   */
  counter_return_travel: number;
}

export interface IActualCountersResult {
  /**
   * Etat dans lequel se trouve le chrono
   */
  counters: ICounters;

  /**
   * Chrono
   */
  chrono: IChrono;
}

export enum TypeChrono {
  Ot = 'ot',
  ActiviteInterne = 'activite interne',
}

/**
 * Enum représentant les différentes actions pour les chronos
 */
export enum TypeAction {
  Intervention = 'intervention',
  Travel = 'travel',
}

/**
 * Nombre maximum de chronos simultanés
 */
const maxChronos = 2;

/**
 * Provider pour la gestion du chronomètre
 *
 * @export
 * @class ChronosProvider
 */
@Injectable({ providedIn: 'root' })
export class ChronosProvider {
  /**
   * Tableau des chronos actifs
   *
   */
  chronos: IChrono[] = [];

  /**
   * Logger
   */
  private logger: LoggerWrapper;

  /**
   * Reference sur le loader pour la redirection
   */
  loadingControl: HTMLIonLoadingElement;

  /**
   * Constructeur
   *
   * Créé une instance de ChronosProvider.
   *
   * @param {StorageProvider} storageProvider
   * @param {Events} events
   * @param loggerFactory
   * @param stiChronoProvider
   * @param translate
   * @param alertCtrl
   * @param loadingCtrl
   * @param router
   * @param utilService
   * @param demandesStiProvider
   */
  constructor(
    private storageProvider: StorageProvider,
    private events: Events,
    private loggerFactory: LoggerFactory,
    private stiChronoProvider: StiProvider,
    private translate: TranslateService,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private router: Router,
    private utilService: UtilsServices,
    private demandesStiProvider: OtWithStiProvider
  ) {
    this.logger = this.loggerFactory.getInstance(this);
    this.refreshChronosList().then();
    // Hide loading quand on arrive termine la redirection chrono
    this.events.subscribe('hideChronoLoading', () => {
      this.hideLoading();
    });
  }

  /**
   * Réinitialise le tableau de chrono
   *
   */
  deleteChronos() {
    this.chronos = [];
  }

  /**
   * Récupère le tableau de chronos
   *
   * @returns tableau de chronos
   */
  getChronos(): IChrono[] {
    return this.chronos;
  }

  /**
   * Modifie le tableau de chronos
   *
   * @returns tableau de chronos
   */
  setChronos(chronos) {
    this.chronos = chronos;
    this.events.publish('refreshTab', Date.now());
  }

  /**
   * Ajoute un chrono au tableau de chronos et le lance
   */
  async addChrono(chronoInput: ChronoInput, action: string) {
    await this.refreshChronosList();
    let chronoExist: number;
    if (chronoInput.unplanned) {
      chronoExist = this.checkChronoExist(chronoInput.idOt, chronoInput.id_site, chronoInput.unplanned);
    } else {
      chronoExist = this.checkChronoExist(chronoInput.idOt);
    }
    if (chronoExist >= 0) return;

    this.chronos.push(
      this.createChrono(
        TypeChrono.Ot,
        chronoInput.idOt,
        chronoInput.libelleOt,
        chronoInput.id_site,
        chronoInput.site_name || '',
        chronoInput.unplanned,
        chronoInput.isStiCreated,
        chronoInput.isSiu,
        chronoInput.idSti,
        chronoInput.canalCreationSti,
        chronoInput.natureSti,
        chronoInput.isIoT,
        chronoInput.dkCodeSite,
        null,
        chronoInput.typeActiviteInterne
      )
    );

    await this.startStopChrono(chronoInput.idOt, action, chronoInput.id_site);
    await this.saveAndDispatchChronos();
    this.chronos.reverse();
    if (this.chronos.length >= 1) {
      this.chronos[0].activeChrono = 'activeChrono';
    }
    if (this.chronos.length > 1) {
      this.chronos[1].activeChrono = 'inactiveChrono';
    }
  }

  /**
   * Ajoute un chrono d'activité interne au tableau de chronos et le lance
   * @param id id de l'activité interne
   * @param idTempo id tempo
   * @param typeActivite type de l'activité interne
   * @param action action de demarrage du chrono
   * @param estRenfort
   */
  async addChronoActiviteInterne(
    id: string,
    idTempo: string,
    typeActivite: string,
    action: TypeAction,
    estRenfort: boolean = false
  ) {
    await this.refreshChronosList();

    if (this.checkChronoActiviteExist(idTempo) < 0) {
      this.chronos.unshift(
        this.createChrono(
          TypeChrono.ActiviteInterne,
          id,
          this.translate.instant(estRenfort ? 'Renfort' : 'Activité interne'),
          null,
          '',
          false,
          false,
          false,
          null,
          null,
          null,
          false,
          null,
          idTempo,
          typeActivite,
          estRenfort
        )
      );
      await this.startStopChrono(id, action, null);
      await this.saveAndDispatchChronos();
    } else {
      const alert = await this.alertCtrl.create({
        header: this.translate.instant('CHRONO.TOO_MUCH_CHRONOS_ACTIVITE_INTERNE_TITLE'),
        message: this.translate.instant('CHRONO.TOO_MUCH_CHRONOS_ACTIVITE_INTERNE_MESSAGE'),
        buttons: [this.translate.instant('GLOBAL.OK')],
      });
      await alert.present();
    }
  }

  /**
   * Ajoute un chrono d'activité interne à partir d'un CR en cours de saisie
   * @param cr CrActiviteInterne
   */
  async addChronoActiviteInterneFromCr(cr: CrActiviteInterne) {
    await this.refreshChronosList();

    if (this.checkChronoActiviteExist(cr.idTempo) < 0) {
      let chrono: IChrono = this.createChrono(
        TypeChrono.ActiviteInterne,
        cr.id_activite_interne,
        this.translate.instant(cr.estRenfort ? 'Renfort' : 'Activité interne'),
        null,
        '',
        false,
        false,
        false,
        null,
        null,
        null,
        false,
        null,
        cr.idTempo,
        cr.cr_type_activite_realisee,
        cr.estRenfort
      );

      chrono = {
        ...chrono,
        start_date_intervention: moment(cr.cr_activite_interne_date_debut).format(),
        end_date_intervention: moment(cr.cr_activite_interne_date_fin).format(),
        counter_intervention: cr.cr_activite_interne_duree,
        start_date_travel: moment(cr.cr_deplacement_date_debut).format(),
        end_date_travel: moment(cr.cr_deplacement_date_fin).format(),
        counter_travel: cr.cr_deplacement_duree,
      };
      this.chronos.unshift(chrono);
      await this.startStopChrono(cr.id_activite_interne, TypeAction.Intervention, null);
      await this.saveAndDispatchChronos();
    }
  }

  private async saveAndDispatchChronos() {
    await this.saveChronosInStorage();
    await this.refreshChronosList();
    this.events.publish('refreshTab', Date.now());
  }

  /**
   * Créé un chrono
   *
   * @param typeChrono
   * @param {any} ot_id id de l'ordre de travail associé au chrono
   * @param ot_libelle
   * @param id_site
   * @param site_name
   * @param unplanned
   * @param {boolean} isStiCreated Détermine si le STI de l'OT est créé
   * @param {boolean} isSiu Détermine si l'ot est associé à un STI avec astreinte
   * @param {string} idSti identifiant du STI
   * @param canalCreationSti
   * @param {string} natureSti nature du STI (GLOBAL / PAS_A_PAS)
   * @param isIoT
   * @param dkCodeSite
   * @param idTempo
   * @param typeActiviteInterne
   * @param estRenfort
   * @returns l'objet chrono
   *
   */
  createChrono(
    typeChrono: TypeChrono,
    ot_id: string,
    ot_libelle: string,
    id_site: string,
    site_name: string,
    unplanned: boolean,
    isStiCreated: boolean,
    isSiu: boolean,
    idSti: string,
    canalCreationSti: string,
    natureSti: string,
    isIoT: boolean,
    dkCodeSite: string,
    idTempo?: string,
    typeActiviteInterne?: string,
    estRenfort: boolean = false
  ): IChrono {
    return {
      typeChrono: typeChrono,
      idOt: ot_id,
      idTempo: idTempo,
      libelleOt: ot_libelle,
      typeActiviteInterne: typeActiviteInterne,
      id_site: id_site,
      site_name: site_name,
      start_travel: 0,
      counter_travel: 0,
      start_intervention: 0,
      counter_intervention: 0,
      active_travel: false,
      active_intervention: false,
      start_return_travel: 0,
      counter_return_travel: 0,
      active_return_travel: false,
      unplanned: unplanned,
      isStiCreated: isStiCreated,
      isSiu: isSiu,
      state: undefined,
      idSti: idSti,
      canalCreationSti: canalCreationSti,
      natureSti: natureSti,
      isIoT,
      dkCodeSite,
      estRenfort,
    };
  }

  /**
   * Vérifie si le chrono existe déjà et sa position dans le tableau de chronos
   *
   * @param ot_id id de l'ordre de travail associé au chrono
   * @param id_site
   * @param unplanned
   * @returns index du chrono dans le tableau ou -1 s'il n'existe pas
   */
  checkChronoExist(ot_id: string, id_site: string = null, unplanned = null): number {
    // this.logger.log('checkChronoExist ' + ot_id + " : ", this.chronos);
    for (let index = 0; index < this.chronos.length; index++) {
      if (unplanned) {
        if (this.chronos[index].id_site === id_site && this.chronos[index].unplanned === unplanned) return index;
      } else {
        if (this.chronos[index].idOt === ot_id) return index;
      }
    }
    return -1;
  }

  getChronoByIdOt(ot_id: string): IChrono | null {
    return this.chronos.find((chrono: IChrono) => chrono.idOt === ot_id) || null;
  }

  /**
   * Vérifie si l'activité interne à un chrono de lancé et retourne sa position dans le tableau
   * @param idTempo id tempo
   */
  checkChronoActiviteExist(idTempo: string): number {
    return this.chronos.findIndex(
      (chrono) => chrono.typeChrono === TypeChrono.ActiviteInterne && chrono.idTempo === idTempo
    );
  }

  /**
   * Lance la lecture ou met en pause le chrono selectionné
   *
   * @param ot_id id de l'odre de travail selectionné
   * @param [action=null] action du chrono (travel/intervention/null) si null c'est qu'il s'agit d'un chrono qui se termine complétement
   * @param id_site
   */
  async startStopChrono(ot_id: string, action = null, id_site: string) {
    let actualTime: number = Date.now();
    this.chronos.map((chrono) => {
      // Vérification de la nulité pour les actions sur les activités internes provenant du pulgin TempsParTop
      if ((chrono.idOt === ot_id || !chrono.idOt) && (chrono.id_site === id_site || !chrono.id_site)) {
        switch (action) {
          case 'travel':
            if (this.travelChronoNotYetStarted(chrono) && chrono.active_intervention) {
              this.startStopChrono(ot_id, 'intervention', id_site);
            }

            if (chrono.active_travel) {
              chrono.counter_travel += (actualTime - chrono.start_travel) / 1000;
              chrono.active_travel = false;
              chrono.state = 'En déplacement (pause)';
              chrono.end_date_travel = moment().format();
              if (chrono.isStiCreated) {
                this.stiChronoProvider.actionSTI(chrono.idSti, ActionEtapeSTI.SUSPENSION);
              }
            } else if (chrono.active_return_travel) {
              chrono.counter_return_travel += (actualTime - chrono.start_return_travel) / 1000;
              chrono.active_return_travel = false;
              chrono.state = 'En déplacement (pause)';
              chrono.end_date_return_travel = moment().format();
              if (chrono.isStiCreated) this.stiChronoProvider.actionSTI(chrono.idSti, ActionEtapeSTI.SUSPENSION);
            } else {
              if (this.isSTIGlobal(chrono)) {
                this.stiChronoProvider.actionSTI(chrono.idSti, ActionEtapeSTI.REDEMARRAGE);
              } else if (chrono.isStiCreated && chrono.start_travel > 0) {
                if (chrono.active_intervention && chrono.counter_return_travel == 0 && !this.isSTIGlobal(chrono))
                  this.stiChronoProvider.actionSTI(chrono.idSti, ActionEtapeSTI.CLOTURE_ETAPE);
                else this.stiChronoProvider.actionSTI(chrono.idSti, ActionEtapeSTI.REDEMARRAGE);
              }
              if (chrono.active_intervention == true) {
                chrono.active_intervention = false;
                chrono.counter_intervention += (actualTime - chrono.start_intervention) / 1000;
              }
              chrono.state = 'En déplacement';
              if (this.isReturnStep(chrono)) {
                // trajet retour : "S'il y a eu une étape "Intervention" du STI lancée, alors l'étape "Retour" du STI se lance"
                if (!chrono.start_date_return_travel) {
                  chrono.start_date_return_travel = moment().format();
                }
                chrono.start_return_travel = actualTime;
                chrono.active_return_travel = true;
              } else {
                if (!chrono.start_date_travel) {
                  chrono.start_date_travel = moment().format();
                }
                chrono.start_travel = actualTime;
                chrono.active_travel = true;
              }
            }
            break;
          case 'intervention':
            if (this.travelChronoAlreadyStarted(chrono) && !chrono.active_intervention) {
              this.startStopChrono(ot_id, 'travel', id_site);
            }

            if (chrono.active_intervention) {
              chrono.counter_intervention += (actualTime - chrono.start_intervention) / 1000;
              chrono.active_intervention = false;
              chrono.state = 'En intervention (pause)';
              chrono.end_date_intervention = moment().format();
              if (chrono.isStiCreated) {
                this.stiChronoProvider.actionSTI(chrono.idSti, ActionEtapeSTI.SUSPENSION);
              }
            } else {
              if (!chrono.start_date_intervention) {
                chrono.start_date_intervention = moment().format();
                if (chrono.isIoT) {
                  this.router.navigate(['/iot'], {
                    state: {
                      dkCodeSite: chrono.dkCodeSite,
                    },
                  });
                }
              }
              chrono.start_intervention = actualTime;
              chrono.active_intervention = true;
              chrono.state = 'En intervention';
              if (chrono.active_travel == true) {
                chrono.active_travel = false;
                chrono.counter_travel += (actualTime - chrono.start_travel) / 1000;
              } else if (chrono.active_return_travel) {
                chrono.active_return_travel = false;
                chrono.counter_return_travel += (actualTime - chrono.start_return_travel) / 1000;
              }

              if (chrono.isStiCreated) {
                if (chrono.counter_intervention > 0 || this.isSTIGlobal(chrono)) {
                  this.stiChronoProvider.actionSTI(chrono.idSti, ActionEtapeSTI.REDEMARRAGE);
                } else if (!this.isSTIGlobal(chrono)) {
                  this.stiChronoProvider.actionSTI(chrono.idSti, ActionEtapeSTI.CLOTURE_ETAPE);
                }
              }
            }
            break;
          default:
            if (chrono.active_travel == true) {
              chrono.state = 'En déplacement (pause)';
              chrono.end_date_travel = moment().format();
              chrono.active_travel = false;
              chrono.counter_travel += (actualTime - chrono.start_travel) / 1000;
            }
            if (chrono.active_intervention == true) {
              chrono.state = 'En intervention (pause)';
              chrono.end_date_intervention = moment().format();
              chrono.active_intervention = false;
              chrono.counter_intervention += (actualTime - chrono.start_intervention) / 1000;
            } else if (chrono.active_return_travel) {
              chrono.state = 'En déplacement (pause)';
              chrono.end_date_return_travel = moment().format();
              chrono.active_return_travel = false;
              chrono.counter_return_travel += (actualTime - chrono.start_return_travel) / 1000;
            }
            break;
        }
        chrono.activeChrono = 'activeChrono';
      } else {
        if (chrono.active_travel == true) {
          chrono.state = 'En déplacement (pause)';
          chrono.end_date_travel = moment().format();
          chrono.active_travel = false;
          chrono.counter_travel += (actualTime - chrono.start_travel) / 1000;
        }
        if (chrono.active_intervention == true) {
          chrono.state = 'En intervention (pause)';
          chrono.end_date_intervention = moment().format();
          chrono.active_intervention = false;
          chrono.counter_intervention += (actualTime - chrono.start_intervention) / 1000;
        }
        if (chrono.active_return_travel) {
          chrono.state = 'En déplacement (pause)';
          chrono.end_date_return_travel = moment().format();
          chrono.active_return_travel = false;
          chrono.counter_return_travel += (actualTime - chrono.start_return_travel) / 1000;
        }

        chrono.activeChrono = 'inactiveChrono';
      }

      if (this.isChronoStartedOrRestarted(chrono) && chrono.isStiCreated === true) {
        this.demandesStiProvider.activateSti(chrono.idOt);
        this.logger.info('chrono for ot ' + chrono.libelleOt + ' is started or restarted.');
      }
    });

    await this.saveChronosInStorage();

    this.events.publish('refreshTab', Date.now());
  }

  private isChronoStartedOrRestarted(chrono: IChrono): boolean {
    return chrono.active_travel || chrono.active_return_travel || chrono.active_intervention;
  }

  private travelChronoAlreadyStarted(chrono: IChrono) {
    return chrono.active_travel || chrono.active_return_travel;
  }

  private travelChronoNotYetStarted(chrono: IChrono) {
    return !chrono.active_travel && !chrono.active_return_travel;
  }

  async removeChrono(chrono: IChrono) {
    let activeIndex = this.chronos.findIndex((chronoNew) => chrono.idOt === chronoNew.idOt);
    this.chronos.splice(activeIndex, 1);
    await this.saveChronosInStorage();
  }

  /**
   * Arrêt total d'un chrono
   *
   * @param {IChrono} chrono chrono à arrêter
   * @returns le comte rendu comprenant les informations du chrono
   */
  async stopChrono(chrono: IChrono) {
    if (chrono == null) return;
    let cr: CrActiviteInterne | any;

    await this.startStopChrono(chrono.idOt, '', chrono.id_site);
    if (chrono.isSiu && chrono.counter_return_travel <= 0) {
      // Dans le cas de STI avec astreinte: En cliquant sur l'icône "Terminer" du bandeau des temps ou sur le bouton "Valider le CR" depuis le détail de l'OT, affichage d'une pop-in: "Voulez-vous démarrer le trajet de retour?" avec les réponses "Oui" et "Non".
      try {
        await this.stiChronoProvider.alertReturnTravel();
        // Le chrono de déplacement se remet en route
        await this.startStopChrono(chrono.idOt, 'travel', chrono.id_site);
        return new Promise((_resolve, reject) => {
          reject('continue chrono');
        });
      } catch (_error) {
        this.logger.log('User click no', _error);
      }
    }

    if (chrono) {
      let chronos = await this.storageProvider.get('chronos', false);
      chrono = chronos.find((chronoNew) => chrono.idOt === chronoNew.idOt);
    }

    if (chrono) {
      chrono.id = chrono.idOt;
      this.storageProvider.addDraft(chrono, 'chronoWaiting');
    }

    switch (chrono.typeChrono) {
      case TypeChrono.Ot:
        cr = {
          date_modification_intervenant: moment().format(),
          date_modification_mop: moment().format(),
          intervention_date_debut: chrono.start_date_intervention || moment().format(),
          intervention_date_fin: chrono.end_date_intervention || moment().format(),
          intervention_duree: this.utilService.hhmm(chrono.counter_intervention),
          deplacement_date_debut: chrono.start_date_travel || moment().format(),
          deplacement_date_fin: chrono.end_date_return_travel || chrono.end_date_travel || moment().format(),
          deplacement_duree: this.utilService.hhmm(chrono.counter_travel + chrono.counter_return_travel),
          deplacement_retour_date_debut: chrono.start_date_return_travel,
          deplacement_retour_date_fin: chrono.end_date_return_travel,
          deplacement_retour_duree: this.utilService.hhmm(chrono.counter_return_travel),
          deplacement_aller_date_debut: chrono.start_date_travel,
          deplacement_aller_date_fin: chrono.end_date_travel,
          deplacement_aller_duree: this.utilService.hhmm(chrono.counter_travel),
          duree_totale: this.utilService.hhmm(
            chrono.counter_intervention + chrono.counter_travel + chrono.counter_return_travel
          ),
        };
        break;
      case TypeChrono.ActiviteInterne:
        cr = {
          id_activite_interne: chrono.idOt,
          cr_commentaire_intervenant: null,
          cr_type_activite_realisee: chrono.typeActiviteInterne,
          cr_activite_interne_date_debut: chrono.start_date_intervention || Date.now(),
          cr_activite_interne_date_fin: chrono.end_date_intervention || Date.now(),
          cr_activite_interne_duree_in_seconds: chrono.counter_intervention,
          cr_deplacement_date_debut: chrono.start_date_travel || Date.now(),
          cr_deplacement_date_fin: chrono.end_date_travel || Date.now(),
          cr_deplacement_duree_in_seconds: chrono.counter_travel,
          cr_duree_totale_in_seconds: chrono.counter_intervention + chrono.counter_travel,
          idTempo: chrono.idTempo,
          estRenfort: chrono.estRenfort,
        };
        break;

      default:
        break;
    }

    return cr;
  }

  /**
   * Modifie les informations STI du chrono
   * associé à l'ot précisé
   * @param ot_id Identifiant de l'ordre de travail
   * @param {boolean} isStiCreated Détermine si le STI de l'OT est créé
   * @param {boolean} isSiu Détermine si l'ot est associé à un STI avec astreinte
   * @param {string} idSti identifiant du STI
   * @param {string} natureSti nature du STI (pas à pas ou global)
   */
  async setStiChrono(ot_id, isStiCreated: boolean, isSiu: boolean, idSti?: string, natureSti?: string) {
    const chronoIndex: number = this.checkChronoExist(ot_id);
    if (chronoIndex > -1) {
      const chrono: IChrono = this.chronos[chronoIndex];
      chrono.isSiu = isSiu;
      chrono.isStiCreated = isStiCreated;
      chrono.idSti = idSti;
      chrono.natureSti = natureSti;

      await this.saveChronosInStorage();
      await this.refreshChronosList();
      this.events.publish('refreshTab', Date.now());
    }
  }

  /**
   * Enregistre le tableau de chrono dans la mémoire du téléphone
   *
   */
  async saveChronosInStorage() {
    // this.logger.log(this.chronos);
    try {
      await this.storageProvider.set('chronos', this.chronos, false);
    } catch (error) {
      this.logger.warn('error saveChronosInStorage', error);
    }
  }

  /**
   * Met à jour le tableau de chronos
   *
   */
  async refreshChronosList() {
    await this.storageProvider
      .get('chronos', false)
      .then((chronos) => {
        if (chronos == null || chronos.length == 0 || !Array.isArray(chronos)) {
          this.chronos = [];
        } else this.chronos = chronos;
      })
      .catch((error) => {
        this.logger.warn('error get chronos', error);
      });
  }

  /**
   * Récupère la durée totale du chrono passée jusque là
   * @param ot_id l'identifiant de l'ot associé au chrono
   * @returns la durée totale passée depuis le début du chrono
   */
  getTotalDuration(ot_id: string): number {
    const actualCounters = this.getActualCounters(ot_id).counters;
    return actualCounters.counter_intervention + actualCounters.counter_travel + actualCounters.counter_return_travel;
  }

  /**
   * Retourne l'avancement actuel du chrono
   * @returns {IActualCountersResult} l'état dans lequel est le chrono ainsi que le chrono en lui-même
   * @param ot_id
   */
  getActualCounters(ot_id: string): IActualCountersResult {
    // this.logger.log("getActualCounters", ot_id);
    const chrono = this.chronos.find((currentValue) => {
      return currentValue.idOt === ot_id;
    });
    if (chrono == null) {
      return null;
    }
    const actualTime: number = Date.now();

    const counters = {
      counter_travel: chrono.counter_travel,
      counter_return_travel: chrono.counter_return_travel,
      counter_intervention: chrono.counter_intervention,
    };
    if (chrono.active_travel) {
      counters.counter_travel += (actualTime - chrono.start_travel) / 1000;
    } else if (chrono.active_return_travel) {
      counters.counter_return_travel += (actualTime - chrono.start_return_travel) / 1000;
    } else if (chrono.active_intervention) {
      counters.counter_intervention += (actualTime - chrono.start_intervention) / 1000;
    }
    return {
      counters,
      chrono,
    };
  }

  /**
   * Vérifie si le chrono correspond à une étape de déplacement retour
   * S'il y a eu une étape "Intervention" du STI lancée, alors l'étape "Retour" du STI se lance
   * @param {IChrono} chrono
   * @returns vrai si le chrono est une étape de retour
   */
  private isReturnStep(chrono: IChrono): boolean {
    return chrono.isStiCreated && chrono.counter_intervention > 0;
  }

  /**
   * Vérifie la nature du STI
   * Il n'y a pas besoin de faire de cloture d'étape pour la Nature STI Global
   * @param {IChrono} chrono
   * @returns vrai si le chrono est une étape de retour
   */
  private isSTIGlobal(chrono: IChrono): boolean {
    return chrono.natureSti === 'global';
  }

  /**
   * Vérifie si le nombre de chronos maximum est atteint
   */
  hasTooMuchChronos(): boolean {
    return this.chronos.length === maxChronos;
  }

  /**
   * Affiche un message d'alert à l'utilisateur sur le nombre de chronos
   */
  async alertTooMuchChronos() {
    const alert = await this.alertCtrl.create({
      message: this.translate.instant('CHRONO.TOO_MUCH_CHRONOS'),
      buttons: [this.translate.instant('GLOBAL.OK')],
    });
    await alert.present();
  }

  /**
   * indique si au moins un chrono IoT est en cours
   */
  isIoTChronoActive(): boolean {
    return this.chronos.some((chrono) => chrono.isIoT && chrono.activeChrono === 'activeChrono');
  }

  /**
   * Stop le premier chrono de type IoT et redirige sur la page du Detail de l'ot Agenda
   */
  stopChronoIoT(): void {
    let chrono: IChrono = this.getChronos().find((chrono) => chrono.isIoT);
    const cr = this.stopChrono(chrono);
    let params = {
      irId: chrono.idOt,
      siteId: chrono.id_site,
      chrono: cr,
    };
    this.redirectToCr(params);
  }

  /**
   * Redirige vers les comptes rendu d'Ot. Empile le Cr sur la page tournée si l'appel ce fait depuis IoT
   */
  async redirectToCr(paramsDetailPage: any) {
    if (this.router.url.includes('/mnt/ots/' + paramsDetailPage.irId + '/cr')) {
      // do nothing because we are already on the cr page of the current ot
      await this.hideLoading();
    } else if (this.router.url.includes('/mnt/ots/' + paramsDetailPage.irId)) {
      this.events.publish('chronoStoppedFromCurrentPage', { chrono: paramsDetailPage.chrono });
      await this.hideLoading();
    } else {
      await this.hideLoading();
      // on fait le trick ci-dessous qui permet de recharger la page detail ot meme quand elle etait deja en memoire.
      // Si on ne fait pas ca, la redirection vers la page cr-edit fait dans ot-detail ne fonctionne pas
      // side effect : on perd l'historique quand on fait back sur la page ot-detail. On revient à la page 'root'
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(async () => {
        await this.presentLoading();
        await this.router.navigate(['/mnt/ots/', paramsDetailPage.irId], {
          queryParams: {
            siteId: paramsDetailPage.siteId,
            chrono: paramsDetailPage.chrono,
          },
        });
      });
    }
  }

  /**
   * Affiche une popin de chargement
   */
  async presentLoading() {
    const loadingValue = this.translate.instant('GLOBAL.LOADING');
    const content = `${loadingValue}...`;
    this.loadingControl = await this.loadingCtrl.create({
      message: content,
    });

    await this.loadingControl.present();
  }

  /**
   * Cache la popin de chargement précédemment ouverte
   * @return {Promise} promesse qui se résoud quand la popin a disparu
   */
  private async hideLoading() {
    if (this.loadingControl != null) {
      await this.loadingControl.dismiss();
      this.loadingControl = null;
    }
  }
}
